const RECEIPT_STAGE_TEXT = {
  processing: 'Procesando tu pedido',
  printing: 'Imprimiendo tu boleta',
  complete: 'Pedido completo',
};

const RECEIPT_PAYMENT_LABELS = {
  PAID: 'Pagado',
  PENDING: 'Pendiente',
  AUTHORIZED: 'Autorizado',
  PARTIALLY_PAID: 'Pago parcial',
  REFUNDED: 'Reembolsado',
  PARTIALLY_REFUNDED: 'Reembolso parcial',
  VOIDED: 'Anulado',
  EXPIRED: 'Expirado',
};

// Paper feeds one "line" at a time, like a thermal printer.
const RECEIPT_PRINT_TRANSFORMS = [
  'translateY(calc(-100% + 2px))',
  'translateY(-91%)',
  'translateY(-91%)',
  'translateY(-81%)',
  'translateY(-81%)',
  'translateY(-70%)',
  'translateY(-70%)',
  'translateY(-58%)',
  'translateY(-58%)',
  'translateY(-45%)',
  'translateY(-45%)',
  'translateY(-32%)',
  'translateY(-32%)',
  'translateY(-20%)',
  'translateY(-20%)',
  'translateY(-10%)',
  'translateY(-10%)',
  'translateY(-3%)',
  'translateY(-3%)',
  'translateY(0%)',
];

const RECEIPT_PRINT_TIMES = [
  0, 0.075, 0.105, 0.18, 0.21, 0.285, 0.315, 0.39, 0.42, 0.495, 0.525, 0.6, 0.63, 0.705, 0.735, 0.81, 0.84, 0.915,
  0.945, 1,
];

const RECEIPT_CLIP_PATH = (() => {
  const teeth = 40;
  const depth = 4;
  const points = Array.from({ length: teeth * 2 }, (_, index) => {
    const x = 100 - ((index + 1) * 100) / (teeth * 2);
    const y = index % 2 === 0 ? '100%' : `calc(100% - ${depth}px)`;
    return `${x}% ${y}`;
  }).join(', ');
  return `polygon(0 0, 100% 0, 100% calc(100% - ${depth}px), ${points})`;
})();

class OrderTrackingChat extends HTMLElement {
  #pollTimer = null;
  #threadId = null;
  #knownMessageCount = 0;
  #receiptTimers = [];
  #receiptAnimation = null;
  #receiptObserver = null;

  connectedCallback() {
    this.apiBase = (this.dataset.apiBase || '').replace(/\/$/, '');

    this.whatsappNumber = (this.dataset.whatsapp || '56934444391').replace(/\D/g, '');

    this.lookupCard = this.querySelector('[data-lookup-card]');
    this.lookupForm = this.querySelector('[data-lookup-form]');
    this.orderNumberInput = this.querySelector('[data-order-number]');
    this.emailInput = this.querySelector('[data-email]');
    this.lookupError = this.querySelector('[data-lookup-error]');
    this.lookupSubmit = this.querySelector('[data-lookup-submit]');

    this.resultPanel = this.querySelector('[data-result-panel]');
    this.orderSummary = this.querySelector('[data-order-summary]');
    this.messagesList = this.querySelector('[data-messages-list]');

    this.chatForm = this.querySelector('[data-chat-form]');
    this.chatInput = this.querySelector('[data-chat-input]');

    this.lookupForm?.addEventListener('submit', (event) => this.#handleLookupSubmit(event));
    this.chatForm?.addEventListener('submit', (event) => this.#handleChatSubmit(event));
    this.querySelector('[data-new-search]')?.addEventListener('click', () => this.#resetSearch());

    this.#prefillFromUrl();
  }

  disconnectedCallback() {
    if (this.#pollTimer) clearInterval(this.#pollTimer);
    this.#stopReceipt();
  }

  // Lets the order-confirmation email link straight to the animated receipt:
  // /pages/seguimiento-de-compra?order=1003&email=cliente@correo.com
  #prefillFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const order = params.get('order');
    const email = params.get('email');
    if (!order || !email || !this.lookupForm) return;

    this.orderNumberInput.value = order;
    this.emailInput.value = email;
    window.history.replaceState(null, '', window.location.pathname);
    this.lookupForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  }

  async #handleLookupSubmit(event) {
    event.preventDefault();
    if (!this.apiBase) {
      this.#showLookupError('El bloque no está configurado todavía (falta la URL del servicio).');
      return;
    }

    const orderNumber = this.orderNumberInput.value.trim();
    const email = this.emailInput.value.trim();
    if (!orderNumber || !email) {
      this.#showLookupError('Completa tu número de pedido y tu correo para buscar.');
      (orderNumber ? this.emailInput : this.orderNumberInput).focus();
      return;
    }
    if (!this.emailInput.checkValidity()) {
      this.#showLookupError('Revisa tu correo: parece que no está bien escrito.');
      this.emailInput.focus();
      return;
    }

    this.#setLoading(true);
    this.#showLookupError('');

    try {
      const response = await fetch(`${this.apiBase}/api/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_number: orderNumber, email }),
      });
      const data = await response.json();

      if (!response.ok) {
        this.#showLookupError(
          data.error === 'not_found'
            ? 'No encontramos un pedido con ese número y correo. Revisa los datos e intenta de nuevo.'
            : 'Ocurrió un problema buscando tu pedido. Intenta de nuevo en un momento.'
        );
        return;
      }

      this.#threadId = data.thread_id;
      this.#renderOrderSummary(data.order, data.shipping_stages, data.current_stage_index);
      this.resultPanel.hidden = false;
      (this.lookupCard || this.lookupForm).hidden = true;
      this.resultPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });

      await this.#loadMessages();
      this.#startPolling();
    } catch {
      this.#showLookupError('No pudimos conectar con el servicio. Intenta de nuevo en un momento.');
    } finally {
      this.#setLoading(false);
    }
  }

  #renderOrderSummary(order, stages, currentStageIndex) {
    this.orderName = order.name;
    const trackingHtml = order.tracking?.length
      ? `<div class="otc__tracking">${order.tracking
          .map(
            (t) =>
              `<p>${this.#escape(t.company || 'Transportista')}: ${
                t.url ? `<a href="${this.#escape(t.url)}" target="_blank" rel="noopener">${this.#escape(t.number || 'Ver seguimiento')}</a>` : this.#escape(t.number || '')
              }</p>`
          )
          .join('')}</div>`
      : '';

    const timelineHtml = this.#renderStageTimeline(stages, currentStageIndex);
    const packagePhotosHtml = Array.isArray(order.package_photos) && order.package_photos.length
      ? `<section class="otc__package-photos"><h3>Fotos de tu paquete</h3><div>${order.package_photos
          .map((photo) => `<a href="data:${this.#escape(photo.content_type)};base64,${photo.image_data}" target="_blank" rel="noopener"><img src="data:${this.#escape(photo.content_type)};base64,${photo.image_data}" alt="Foto de tu paquete" loading="lazy"></a>`)
          .join('')}</div></section>`
      : '';

    this.orderSummary.innerHTML = `
      ${Array.isArray(order.items) && order.items.length ? this.#renderReceipt(order) : ''}
      ${timelineHtml}
      ${packagePhotosHtml}
      ${trackingHtml}
    `;
    this.#playReceipt();
  }

  #formatMoney(value, currency = 'CLP') {
    try {
      return new Intl.NumberFormat('es-CL', { style: 'currency', currency, maximumFractionDigits: 0 }).format(value);
    } catch {
      return String(value);
    }
  }

  #renderReceipt(order) {
    const hasMoney = typeof order.total === 'number';
    const money = (value) => this.#formatMoney(value, order.currency);
    const line = (label, value, extraClass = '') =>
      `<div class="ds-printer__line ${extraClass}"><span>${label}</span><span>${value}</span></div>`;

    const date = order.created_at
      ? new Date(order.created_at).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : '';
    const payment = RECEIPT_PAYMENT_LABELS[order.financial_status] || order.financial_status || '';

    const rowsHtml = (order.items || [])
      .map((item) => {
        const variant = item.variant ? `<small>${this.#escape(item.variant)}</small>` : '';
        const amount =
          hasMoney && typeof item.total === 'number'
            ? `<span class="ds-printer__row-amount">${money(item.total)}</span>`
            : '';
        return `<li class="ds-printer__row"><span class="ds-printer__row-name">${item.quantity} × ${this.#escape(
          item.title
        )}${variant}</span>${amount}</li>`;
      })
      .join('');

    // Shopify's subtotal is already net of discounts, so rebuild it from the line items.
    const itemsSum = (order.items || []).reduce(
      (sum, item) => sum + (typeof item.total === 'number' ? item.total : 0),
      0
    );
    const subtotal = itemsSum > 0 ? itemsSum : order.subtotal;

    const totalsHtml = hasMoney
      ? `<hr class="ds-printer__rule">
         <div class="ds-printer__totals">
           ${line('Subtotal', money(subtotal))}
           ${order.discounts > 0 ? line('Descuento', `-${money(order.discounts)}`) : ''}
           ${line('Envío', order.shipping > 0 ? money(order.shipping) : 'Gratis')}
           ${line('TOTAL', money(order.total), 'ds-printer__line--total')}
         </div>`
      : '';

    return `
      <section class="ds-printer" data-receipt-printer data-stage="processing" aria-label="Boleta de tu pedido">
        <div class="ds-printer__machine">
          <div class="ds-printer__header">
            <span class="ds-printer__brand">DRIPSTORE.CL</span>
            <span class="ds-printer__led" aria-hidden="true"></span>
          </div>
          <div class="ds-printer__screen">
            <div class="ds-printer__status">
              <span class="ds-printer__indicator" aria-hidden="true">
                <svg class="ds-printer__spinner" viewBox="0 0 256 256" width="18" height="18" fill="currentColor"><path d="M232,128a104,104,0,0,1-208,0c0-41,23.81-78.36,60.66-95.27a8,8,0,0,1,6.68,14.54C60.15,61.59,40,93.27,40,128a88,88,0,0,0,176,0c0-34.73-20.15-66.41-51.34-80.73a8,8,0,0,1,6.68-14.54C208.19,49.64,232,87,232,128Z"/></svg>
                <svg class="ds-printer__check" viewBox="0 0 256 256" width="18" height="18" fill="currentColor"><path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm45.66,85.66-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35a8,8,0,0,1,11.32,11.32Z"/></svg>
              </span>
              <div class="ds-printer__label" role="status" aria-live="polite" data-receipt-label>${RECEIPT_STAGE_TEXT.processing}</div>
            </div>
          </div>
          <div class="ds-printer__slot" aria-hidden="true"></div>
        </div>
        <div class="ds-printer__output" data-receipt-output>
          <div class="ds-printer__shade" aria-hidden="true"></div>
          <div class="ds-printer__feed" data-receipt-feed aria-hidden="true">
            <article class="ds-printer__paper" data-receipt-paper style="clip-path: ${RECEIPT_CLIP_PATH}">
              <h3 class="ds-printer__title">Dripstore.cl</h3>
              <p class="ds-printer__subtitle">Boleta de compra</p>
              <hr class="ds-printer__rule">
              ${line('Pedido', this.#escape(order.name))}
              ${date ? line('Fecha', date) : ''}
              ${payment ? line('Pago', this.#escape(payment)) : ''}
              <hr class="ds-printer__rule">
              <ul class="ds-printer__items">${rowsHtml}</ul>
              ${totalsHtml}
              <hr class="ds-printer__rule">
              <p class="ds-printer__thanks">¡Gracias por tu compra!</p>
            </article>
          </div>
        </div>
      </section>`;
  }

  #setReceiptLabel(label, text, animate) {
    if (!animate || label.textContent === text) {
      label.textContent = text;
      return;
    }
    const out = label.animate(
      [
        { opacity: 1, transform: 'translateY(0)' },
        { opacity: 0, transform: 'translateY(-4px)' },
      ],
      { duration: 120, easing: 'ease-out' }
    );
    out.onfinish = () => {
      label.textContent = text;
      label.animate(
        [
          { opacity: 0, transform: 'translateY(4px)' },
          { opacity: 1, transform: 'translateY(0)' },
        ],
        { duration: 180, easing: 'cubic-bezier(0.23, 1, 0.32, 1)' }
      );
    };
  }

  #stopReceipt() {
    this.#receiptTimers.forEach((timer) => clearTimeout(timer));
    this.#receiptTimers = [];
    this.#receiptAnimation?.cancel();
    this.#receiptAnimation = null;
    this.#receiptObserver?.disconnect();
    this.#receiptObserver = null;
  }

  #playReceipt() {
    this.#stopReceipt();

    const printer = this.orderSummary.querySelector('[data-receipt-printer]');
    if (!printer) return;

    const output = printer.querySelector('[data-receipt-output]');
    const feed = printer.querySelector('[data-receipt-feed]');
    const paper = printer.querySelector('[data-receipt-paper]');
    const label = printer.querySelector('[data-receipt-label]');

    // The paper window is as tall as the receipt, so short orders don't leave a big gap.
    const fit = () => {
      output.style.height = `${paper.offsetHeight + 24}px`;
    };
    fit();
    this.#receiptObserver = new ResizeObserver(fit);
    this.#receiptObserver.observe(paper);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const showComplete = () => {
      printer.dataset.stage = 'complete';
      this.#setReceiptLabel(label, RECEIPT_STAGE_TEXT.complete, !reduceMotion);
      feed.style.opacity = '1';
      feed.style.transform = 'none';
      feed.removeAttribute('aria-hidden');
    };

    if (reduceMotion) {
      showComplete();
      return;
    }

    feed.style.opacity = '0';
    feed.style.transform = 'translateY(calc(-100% + 2px))';

    this.#receiptTimers.push(
      setTimeout(() => {
        printer.dataset.stage = 'printing';
        this.#setReceiptLabel(label, RECEIPT_STAGE_TEXT.printing, true);
        feed.style.transition = 'opacity 160ms cubic-bezier(0.23, 1, 0.32, 1)';
        feed.style.opacity = '1';

        this.#receiptAnimation = feed.animate(
          RECEIPT_PRINT_TRANSFORMS.map((transform, index) => ({ transform, offset: RECEIPT_PRINT_TIMES[index] })),
          { duration: 1750, easing: 'linear', fill: 'forwards' }
        );
        this.#receiptAnimation.onfinish = () => {
          this.#receiptAnimation?.cancel();
          this.#receiptAnimation = null;
          showComplete();
        };
      }, 900)
    );
  }

  #renderStageTimeline(stages, currentStageIndex) {
    if (!Array.isArray(stages) || !stages.length) return '';

    // No custom stage set yet by the merchant — nothing meaningful to show
    // as a timeline; the payment status line above already covers it.
    if (currentStageIndex == null || currentStageIndex < 0) return '';

    const check = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>';
    const stepsHtml = stages
      .map((stage, index) => {
        const state = index < currentStageIndex ? 'done' : index === currentStageIndex ? 'current' : 'upcoming';
        // El aviso se muestra solo mientras el pedido est? en una etapa de aduana.
        const delayNoticeHtml = state === 'current' && /aduan/i.test(stage.label || '')
          ? `<div class="otc__delay-notice" role="note">
              <span class="otc__delay-icon" aria-hidden="true">!</span>
              <div class="otc__delay-copy">
                <strong>Posible demora en la entrega</strong>
                <p>El plazo estimado puede extenderse por la acumulación de envíos y las inspecciones de la Aduana de Chile. El seguimiento se actualizará cuando recibamos novedades.</p>
              </div>
            </div>`
          : '';
        return `
          <li class="otc__stage otc__stage--${state}"${state === 'current' ? ' aria-current="step"' : ''}>
            <span class="otc__stage-dot">${state === 'done' ? check : ''}</span>
            <span class="otc__stage-copy">
              <span class="otc__stage-label">${this.#escape(stage.label)}</span>
              ${delayNoticeHtml}
            </span>
          </li>`;
      })
      .join('');

    const step = Math.min(currentStageIndex + 1, stages.length);
    const percent = Math.round((step / stages.length) * 100);

    return `
      <div class="otc__progress">
        <div class="otc__progress-head">
          <p class="otc__progress-label">Estado del envío</p>
          <p class="otc__progress-count">Etapa ${step} de ${stages.length}</p>
        </div>
        <p class="otc__progress-current">${this.#escape(stages[currentStageIndex]?.label)}</p>
        <div class="otc__progress-track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${percent}" aria-label="Avance del envío">
          <div class="otc__progress-fill" style="width: ${percent}%"></div>
        </div>
        <ol class="otc__timeline">${stepsHtml}</ol>
      </div>`;
  }

  #handleChatSubmit(event) {
    event.preventDefault();
    const text = this.chatInput.value.trim();
    if (!text) return;

    this.chatInput.value = '';

    const orderPrefix = this.orderName ? `Pedido ${this.orderName}: ` : '';
    const whatsappUrl = `https://wa.me/${this.whatsappNumber}?text=${encodeURIComponent(orderPrefix + text)}`;
    window.open(whatsappUrl, '_blank', 'noopener');
  }

  async #loadMessages() {
    if (!this.#threadId) return;

    try {
      const response = await fetch(`${this.apiBase}/api/messages?thread_id=${encodeURIComponent(this.#threadId)}`);
      const data = await response.json();
      if (!response.ok) return;

      if (data.messages.length === this.#knownMessageCount) return;
      this.#knownMessageCount = data.messages.length;

      this.messagesList.innerHTML = data.messages
        .map(
          (m) =>
            `<li class="otc__message otc__message--${this.#escape(m.sender)}">${this.#escape(m.text)}</li>`
        )
        .join('');
      this.messagesList.scrollTop = this.messagesList.scrollHeight;
    } catch {
      // Ignore — will retry on next poll tick.
    }
  }

  #startPolling() {
    if (this.#pollTimer) return;
    this.#pollTimer = setInterval(() => this.#loadMessages(), 15000);
  }

  #setLoading(isLoading) {
    if (!this.lookupSubmit) return;
    this.lookupSubmit.disabled = isLoading;
    this.lookupSubmit.setAttribute('aria-busy', String(isLoading));
    const text = this.lookupSubmit.querySelector('.otc__submit-text');
    if (text) {
      text.dataset.label ??= text.textContent;
      text.textContent = isLoading ? 'Buscando tu pedido…' : text.dataset.label;
    }
  }

  #resetSearch() {
    if (this.#pollTimer) clearInterval(this.#pollTimer);
    this.#pollTimer = null;
    this.#stopReceipt();
    this.#threadId = null;
    this.#knownMessageCount = 0;
    this.orderName = null;
    this.orderSummary.innerHTML = '';
    this.messagesList.innerHTML = '';
    this.resultPanel.hidden = true;
    (this.lookupCard || this.lookupForm).hidden = false;
    this.#showLookupError('');
    this.orderNumberInput.value = '';
    this.orderNumberInput.focus();
  }

  #showLookupError(message) {
    if (!this.lookupError) return;
    this.lookupError.textContent = message;
    this.lookupError.hidden = !message;
  }

  #escape(value) {
    const div = document.createElement('div');
    div.textContent = value ?? '';
    return div.innerHTML;
  }
}

customElements.define('order-tracking-chat', OrderTrackingChat);
